import { NavLink } from 'react-router-dom'
import { Home, User, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUnreadTotal } from '@/hooks/useUnreadTotal'

export function BottomNav() {
  const { data: unreadTotal = 0 } = useUnreadTotal()

  const items = [
    { to: '/', icon: Home, label: 'Home', end: true },
    { to: '/profile', icon: User, label: 'Profile', end: false },
    { to: '/chat', icon: MessageCircle, label: 'Chat', end: false, badge: unreadTotal },
    { to: '/bimss', icon: 'logo' as const, label: 'BIMSS', end: false },
  ]

  return (
    <>
      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur lg:hidden">
        <ul className="grid grid-cols-4 max-w-md mx-auto">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex flex-col items-center justify-center gap-1 py-3 transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <div className="relative">
                      {item.icon === 'logo' ? (
                        <BimssLogo active={isActive} />
                      ) : (
                        <item.icon
                          className={cn('h-5 w-5', isActive && 'fill-primary/20')}
                        />
                      )}
                      {'badge' in item &&
                        item.badge !== undefined &&
                        item.badge > 0 && (
                          <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                    </div>
                    <span className={cn('text-[10px]', isActive && 'font-medium')}>
                      {item.label}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Desktop sidebar */}
      <nav className="fixed left-0 top-14 hidden h-[calc(100vh-3.5rem)] w-56 border-r bg-background p-4 lg:block">
        <div className="flex flex-col gap-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'text-muted-foreground hover:bg-accent'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className="relative">
                    {item.icon === 'logo' ? (
                      <BimssLogo active={isActive} size={16} />
                    ) : (
                      <item.icon className="h-4 w-4" />
                    )}
                    {'badge' in item &&
                      item.badge !== undefined &&
                      item.badge > 0 && (
                        <span className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-white text-[8px] font-semibold flex items-center justify-center">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                  </div>
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  )
}

function BimssLogo({ active, size = 24 }: { active: boolean; size?: number }) {
  const scale = size / 28
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      className={active ? 'opacity-100' : 'opacity-70'}
    >
      <circle
        cx="9"
        cy="14"
        r={6 * scale + (1 - scale) * 6}
        fill={active ? '#dc2626' : '#9ca3af'}
        stroke="white"
        strokeWidth="1.5"
      />
      <circle
        cx="19"
        cy="14"
        r={6 * scale + (1 - scale) * 6}
        fill={active ? '#dc2626' : '#9ca3af'}
        stroke="white"
        strokeWidth="1.5"
      />
      <circle cx="9" cy="14" r="2" fill="white" />
      <circle cx="19" cy="14" r="2" fill="white" />
    </svg>
  )
}
